<template>
  <div :style="{ position: 'relative', flex: 1, minWidth: 0 }">
    <button
      ref="triggerRef"
      :style="{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 8px',
        borderRadius: '7px',
        border: `1px solid ${open ? 'var(--p-primary-500)' : 'var(--p-content-border-color)'}`,
        background: 'var(--surface-ground, transparent)',
        color: value ? 'var(--p-text-color)' : 'var(--p-surface-400)',
        fontSize: '12px',
        fontFamily: mono,
        cursor: 'pointer',
      }"
      @click="open = !open"
    >
      <i class="pi pi-calendar" :style="{ fontSize: '11px', color: 'var(--p-surface-400)' }" />
      <span :style="{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ value || placeholder }}</span>
    </button>
    <Teleport to="body">
      <div v-if="open" ref="popRef" :style="{ position: 'fixed', top: `${pos.top}px`, left: `${pos.left}px`, zIndex: 2000 }">
        <CalendarPanel :value="value" @select="onSelect" />
      </div>
    </Teleport>
  </div>
</template>

<script setup>
// Date input that pops a CalendarPanel. Ported from fql-search.jsx DateField.
// Emits change(dateStr | null). The panel is teleported to <body> and positioned
// fixed against the trigger so it escapes the filter sidebar's scroll/overflow
// clipping and stacking context (otherwise it hides behind the content window).
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { mono } from './bits.js'
import CalendarPanel from './CalendarPanel.vue'

defineProps({
  value: { type: String, default: '' },
  placeholder: { type: String, default: '' },
})
const emit = defineEmits(['change'])

const PANEL_W = 244
const PANEL_H = 320 // approximate, for edge-flip decisions

const open = ref(false)
const triggerRef = ref(null)
const popRef = ref(null)
const pos = ref({ top: 0, left: 0 })

function place() {
  const el = triggerRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8))
  // Flip above the trigger when there isn't room below.
  const below = r.bottom + 5
  const top = below + PANEL_H > window.innerHeight ? Math.max(8, r.top - PANEL_H - 5) : below
  pos.value = { top, left }
}

function onDocDown(e) {
  if (triggerRef.value && triggerRef.value.contains(e.target)) return
  if (popRef.value && popRef.value.contains(e.target)) return
  open.value = false
}
function onReflow() {
  if (open.value) place()
}

watch(open, async (v) => {
  if (v) {
    await nextTick()
    place()
    document.addEventListener('mousedown', onDocDown)
    window.addEventListener('scroll', onReflow, true)
    window.addEventListener('resize', onReflow)
  } else {
    document.removeEventListener('mousedown', onDocDown)
    window.removeEventListener('scroll', onReflow, true)
    window.removeEventListener('resize', onReflow)
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocDown)
  window.removeEventListener('scroll', onReflow, true)
  window.removeEventListener('resize', onReflow)
})

function onSelect(d) {
  emit('change', d)
  open.value = false
}
</script>
